import React, { useMemo, Suspense } from 'react';
import { lazyWithRetry as lazy } from 'lazy-with-retry';
import { useMEMTranslation } from '../../utils/text-utils';
import { ProgressRing } from '../../providers/harmony-scope/lazy-enablers';
import { ETabId, TTabConfig } from './tab-types';
import {
  useComplianceTabGuard,
  useEarningsTabGuard,
  useEstimatorTabGuard,
  useImpersonationTabGuard,
  useMyCompensationStoryTabGuard,
  useMyChangeHistoryTabGuard,
  useMyPlanTabGuard,
  useMyTeamTabGuard,
  useNotificationsAdminTabGuard,
  usePerformanceTabGuard,
  usePlanSetupTabGuard,
  usePlanSummaryTabGuard,
} from './tab-guards';
import { ErrorPage } from '../../shared-components/error-page';
import { store } from '../../ducks/store';
import { logTraceToAppInsights, buildICMProperties, trackICMEvent } from '../logging/hooks';
import { EICMType, EICMSeverity } from '../../enums/icm-enum';
import { EBannerType } from '../../ducks/banner-messages/duck-banner-messages';
import i18n from '../../i18n';

const ModuleFetchRetryOptions = (sessionCacheKey: string) => ({
  failFallbackComponent: ErrorPage,
  retries: 3,
  interval: 500,
  forceRefreshOptions: {
    refreshRetries: 1,
    sessionCacheKey,
    cacheKeyPrefix: 'earnings-refetch-key',
  },
  onRefresh: (error: Error) => {
    const impersonatedUser = store.getState().impersonation?.impersonatedUser;
    const pageName = store.getState().filters.activeTab;
    logTraceToAppInsights(
      `Busting cache and trying refresh for failed module import of ${pageName}`,
      pageName || '',
      'SellerExperience',
      impersonatedUser || '',
      error
    );
  },
  onRetry: (error: Error, retriesLeft: number) => {
    const impersonatedUser = store.getState().impersonation?.impersonatedUser;
    const pageName = store.getState().filters.activeTab;
    logTraceToAppInsights(
      `Retrying dynamic import for ${pageName}. Retries left: ${retriesLeft}`,
      pageName || '',
      'SellerExperience',
      impersonatedUser || '',
      error
    );
  },
  onFailure: (error: Error) => {
    const pageName = store.getState().filters.activeTab;
    const icmProps = buildICMProperties(EICMType.General, EICMSeverity.Sev4, undefined, error);
    trackICMEvent('Error loading dynamic imported component', pageName as ETabId, '', icmProps);
    store.dispatch({
      type: 'bannersMessages/addError',
      payload: {
        message: i18n.t('dynamicImportError'),
        bannerType: EBannerType.PageError,
      },
    });
  },
});

const PerformancePageOverloaded = lazy(
  () => import('../../features/tab-performance/page-performance-overloaded'),
  ModuleFetchRetryOptions('PerformancePageOverloaded')
);
const MyTeamPage = lazy(() => import('../../features/tab-my-team/page-my-team'), ModuleFetchRetryOptions('MyTeamPage'));
const EarningsPage = lazy(() => import('../../features/tab-earnings/page-earnings'), ModuleFetchRetryOptions('EarningsPage'));
const ImpersonationPage = lazy(() => import('../../features/tab-impersonation/page-impersonation'), ModuleFetchRetryOptions('ImpersonationPage'));
const PlanSetupPage = lazy(() => import('../../features/tab-plan-setup/page-plan-setup'), ModuleFetchRetryOptions('PlanSetupPage'));
const NotificationsAdminPage = lazy(
  () => import('../../features/tab-notifications-admin/page-notifications-admin'),
  ModuleFetchRetryOptions('NotificationsAdminPage')
);
const CompliancePage = lazy(() => import('../../features/tab-compliance/page-compliance'), ModuleFetchRetryOptions('CompliancePage'));
const PlanSummaryPage = lazy(() => import('../../features/tab-plan-summary/page-plan-summary'), ModuleFetchRetryOptions('PlanSummaryPage'));
const MyChangeHistoryPage = lazy(
  () => import('../../features/tab-my-change-history/page-my-change-history'),
  ModuleFetchRetryOptions('MyChangeHistoryPage')
);
const EstimatorPage = lazy(() => import('../../features/tab-estimator/page-estimator'), ModuleFetchRetryOptions('EstimatorPage'));
const MyCompensationPage = lazy(
  () => import('../../features-v2/my-compensation/page-my-compensation-overloaded'),
  ModuleFetchRetryOptions('MyCompensationPage')
);
const MyPlanPage = lazy(() => import('../../features-v2/my-plan/page-my-plan'), ModuleFetchRetryOptions('MyPlanPage'));

export const DefaultTab = 'PerformancePage';

export const useTabConfig = (): TTabConfig[] => {
  const { t } = useMEMTranslation();

  const myCompensationTabGuard = useMyCompensationStoryTabGuard();
  const myPlanTabGuard = useMyPlanTabGuard();
  const performanceTabGuard = usePerformanceTabGuard();
  const earningsTabGuard = useEarningsTabGuard();
  const planSummaryTabGuard = usePlanSummaryTabGuard();
  const estimatorTabGuard = useEstimatorTabGuard();
  const myTeamTabGuard = useMyTeamTabGuard();
  const complianceTabGuard = useComplianceTabGuard();
  const impersonationTabGuard = useImpersonationTabGuard();
  const planSetupTabGuard = usePlanSetupTabGuard();
  const myChangeHistoryTabGuard = useMyChangeHistoryTabGuard();
  const notificationsAdminTabGuard = useNotificationsAdminTabGuard();

  return useMemo(
    () => [
      {
        tabId: ETabId.MyCompensationTab,
        tabName: t('tabTitleMyCompensation'),
        component: <MyCompensationPage />,
        show: myCompensationTabGuard,
        isNew: true,
      },
      {
        tabId: ETabId.PerformanceTab,
        tabName: t('tabTitlePerformance'),
        component: (
          <Suspense fallback={<ProgressRing indeterminate style={{ margin: '50%' }} />}>
            <PerformancePageOverloaded />
          </Suspense>
        ),
        show: performanceTabGuard,
      },
      {
        tabId: ETabId.EstimatorTab,
        tabName: `${t('tabTitleEstimator')}`,
        component: (
          <Suspense fallback={<ProgressRing indeterminate style={{ margin: '50%' }} />}>
            <EstimatorPage />
          </Suspense>
        ),
        show: estimatorTabGuard,
      },
      {
        tabId: ETabId.EarningsTab,
        tabName: t('tabTitleEarnings'),
        component: (
          <Suspense fallback={<ProgressRing indeterminate style={{ margin: '50%' }} />}>
            <EarningsPage />
          </Suspense>
        ),
        show: earningsTabGuard,
      },
      {
        tabId: ETabId.MyPlanTab,
        tabName: t('tabTitleMyPlan'),
        component: <MyPlanPage />,
        show: myPlanTabGuard,
        isNew: true,
      },
      {
        tabId: ETabId.PlanSummaryTab,
        tabName: t('tabTitlePlanSummary'),
        component: (
          <Suspense fallback={<ProgressRing indeterminate style={{ margin: '50%' }} />}>
            <PlanSummaryPage />
          </Suspense>
        ),
        show: planSummaryTabGuard,
      },
      {
        tabId: ETabId.MyTeamTab,
        tabName: t('tabTitleMyTeam'),
        component: (
          <Suspense fallback={<ProgressRing indeterminate style={{ margin: '50%' }} />}>
            <MyTeamPage />
          </Suspense>
        ),
        show: myTeamTabGuard,
      },
      {
        tabId: ETabId.ComplianceTab,
        tabName: t('tabTitleCompliance'),
        component: (
          <Suspense fallback={<ProgressRing indeterminate style={{ margin: '50%' }} />}>
            <CompliancePage />
          </Suspense>
        ),
        show: complianceTabGuard,
      },
      {
        tabId: ETabId.MyChangeHistoryTab,
        tabName: t('tabTitleMyChangeHistory'),
        component: (
          <Suspense fallback={<ProgressRing indeterminate style={{ margin: '50%' }} />}>
            <MyChangeHistoryPage />
          </Suspense>
        ),
        show: myChangeHistoryTabGuard,
      },
      {
        tabId: ETabId.ImpersonationTab,
        tabName: t('tabTitleImpersonation'),
        component: (
          <Suspense fallback={<ProgressRing indeterminate style={{ margin: '50%' }} />}>
            <ImpersonationPage />
          </Suspense>
        ),
        show: impersonationTabGuard,
      },
      {
        tabId: ETabId.PlanSetupTab,
        tabName: t('tabTitlePlanSetup'),
        component: (
          <Suspense fallback={<ProgressRing indeterminate style={{ margin: '50%' }} />}>
            <PlanSetupPage />
          </Suspense>
        ),
        show: planSetupTabGuard,
      },
      {
        tabId: ETabId.NotificationsAdminTab,
        tabName: t('tabTitleNotificationsAdmin'),
        component: (
          <Suspense fallback={<ProgressRing indeterminate style={{ margin: '50%' }} />}>
            <NotificationsAdminPage />
          </Suspense>
        ),
        show: notificationsAdminTabGuard,
      },
    ],
    [
      t,
      myCompensationTabGuard,
      myPlanTabGuard,
      performanceTabGuard,
      estimatorTabGuard,
      earningsTabGuard,
      planSummaryTabGuard,
      myTeamTabGuard,
      complianceTabGuard,
      impersonationTabGuard,
      planSetupTabGuard,
      myChangeHistoryTabGuard,
      notificationsAdminTabGuard,
    ]
  );
};
